import React, { useMemo, useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { TableRow } from "@mui/material";
import styles from "./OverviewLossRecovery.module.css";

// parts
import OverviewToolbar from "./OverviewToolbar";
import HeaderRow from "./HeaderRow";
import LeftBodyPane from "./LeftBodyPane";
import RightBodyPane from "./RightBodyPane";
import PaginationFooter from "./PaginationFooter";

// utils
import {
  FIXED_LABELS,
  FIXED_WIDTHS,
  getGroupObj,
  getChildVal,
  parseCustomization,
  makeFixedColumns,
  sumFixedPaneWidth,
  deriveChildrenKeysFactory,
  buildScrollableGroups,
  formatValue,
} from "./overviewUtils";

/* ------------------------------ Mini Hooks ------------------------------ */

// body -> header horizontal sync
const useSyncHorizontalScroll = (bodyRef, headerRef) => {
  useEffect(() => {
    const body = bodyRef.current, header = headerRef.current;
    if (!body || !header) return;
    const onH = () => (header.scrollLeft = body.scrollLeft);
    body.addEventListener("scroll", onH, { passive: true });
    return () => body.removeEventListener("scroll", onH);
  }, [bodyRef, headerRef]);
};

// left <-> right vertical sync
const useSyncVerticalScroll = (leftRef, rightRef) => {
  useEffect(() => {
    const L = leftRef.current, R = rightRef.current;
    if (!L || !R) return;
    let lock = null;
    const onR = () => {
      if (lock === "L") {
        return;
      }
      lock = "R";
      L.scrollTop = R.scrollTop;
      lock = null;
    };
    const onL = () => {
      if (lock === "R") {
        return;
      }
      lock = "L";
      R.scrollTop = L.scrollTop;
      lock = null;
    };
    L.addEventListener("scroll", onL, { passive: true });
    R.addEventListener("scroll", onR, { passive: true });
    return () => { L.removeEventListener("scroll", onL); R.removeEventListener("scroll", onR); };
  }, [leftRef, rightRef]);
};

// header paddingRight = body scrollbar width
const useHeaderScrollbarCompensation = (headerRef, bodyRef) => {
  useEffect(() => {
    const header = headerRef.current, body = bodyRef.current;
    if (!header || !body) return;
    const apply = () => (header.style.paddingRight = `${body.offsetWidth - body.clientWidth || 0}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(body);
    return () => ro.disconnect();
  }, [headerRef, bodyRef]);
};

// scroll both panes to top on page/size change
const useScrollToTopOnPageChange = (leftRef, rightRef, deps) => {
  useEffect(() => {
    try {
      if (leftRef.current) leftRef.current.scrollTop = 0;
      if (rightRef.current) rightRef.current.scrollTop = 0;
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/* ---------------------------- Main Component ---------------------------- */

export default function OverviewLossRecovery({
  overview,
  height = 420,
  onActionSuggestedChange,
  onSortChange,
  sortState = {},
  loading,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onCustomize,
  columns,
  onExport,
  exportLoading,
  selectedAction,
  onSelectView,
  selectedView
}) {
  const [actionSuggested, setActionSuggested] = useState(selectedAction);
  const [showExpanded, setShowExpanded] = useState(false);
  const [fxWidth, setFxWidth] = useState({});   // e.g., { 'Basepack Desc': 400 }
  const normalizedOverview = useMemo(
    () => (overview && !Array.isArray(overview) ? overview : {}),
    [overview]
  );

  const page = pagination?.page || 1;
  const pageSize = pagination?.pageSize || 10;
  const totalPages = pagination?.totalPages || 1;

  const rows = useMemo(() => normalizedOverview?.data ?? [], [normalizedOverview]);
  const totalRow = useMemo(() => normalizedOverview?.Total?.[0] ?? null, [normalizedOverview]);
  const columnsInput = useMemo(
    () => (Array.isArray(columns) && columns.length ? columns : (normalizedOverview?.defaultColumns ?? [])),
    [columns, normalizedOverview]
  );

  // fixed + groups derivation
  const { orderedGroups, orderedChildren, fixedLabels } = useMemo(
    () => parseCustomization(columnsInput, FIXED_LABELS),
    [columnsInput]
  );
  const fixedColumns = useMemo(
    () => makeFixedColumns(fixedLabels, FIXED_WIDTHS, fxWidth),
    [fixedLabels, fxWidth]
  );
  const groupLabels = orderedGroups;

  // children keys + scrollable groups
  const deriveChildrenKeys = useMemo(
    () => deriveChildrenKeysFactory(rows, totalRow, { orderedChildren }),
    [rows, totalRow, orderedChildren]
  );
  const scrollableGroups = useMemo(
    () => buildScrollableGroups(groupLabels, deriveChildrenKeys),
    [groupLabels, deriveChildrenKeys]
  );

  // layout
  const FIXED_HEADER_HEIGHT = 56, GROUP_BAR_HEIGHT = 28, SUBHEADER_HEIGHT = 26, ROW_HEIGHT = 36;
  const fixedPaneWidth = useMemo(() => sumFixedPaneWidth(fixedColumns), [fixedColumns]);
  const rightPaneWidth = `calc(100% - ${fixedPaneWidth}px)`;

  // refs
  const rightHeaderRef = useRef(null), rightBodyRef = useRef(null), leftBodyRef = useRef(null);

  // sync & adjustments
  useSyncHorizontalScroll(rightBodyRef, rightHeaderRef);
  useSyncVerticalScroll(leftBodyRef, rightBodyRef);
  useHeaderScrollbarCompensation(rightHeaderRef, rightBodyRef);
  useScrollToTopOnPageChange(leftBodyRef, rightBodyRef, [page, pageSize]);

  const actionSuggestedState = normalizedOverview?.["Action Suggested"];

  // server-selected actions -> local selection
  useEffect(() => {
    const serverSelected = actionSuggestedState?.selected_action_filters;
    const opts = actionSuggestedState?.action_filters_data || [];
    if (Array.isArray(serverSelected)) {
      setActionSuggested(serverSelected.filter(v => opts.includes(v)));
    } else if (opts.length) {
      setActionSuggested([...opts]);
    } else {
      setActionSuggested([]);
    }
  }, [normalizedOverview, actionSuggestedState]);

  const handleActionSuggestedChange = (values) => {
    setActionSuggested(values);
    onActionSuggestedChange?.(values || []);
  };

  // fixed width expand/shrink toggle
  const toggleFixedWidth = (label) => {
    const base = FIXED_WIDTHS[label] ?? 120;
    const expanded = (label === "Basepack Desc" || label === "Action Recommended" || label === "Format")
      ? Math.max(base, 360)
      : base + 100;
    setFxWidth((prev) => {
      const next = { ...prev };
      if (next[label]) {
        delete next[label];
      } else {
        next[label] = expanded;
      }
      return next;
    });
  };

  // sort handlers (delegated to parent)
  const handleSortLeft = (label) => onSortChange?.({ sortBy: label, fieldsection: "" });
  const handleSortRightChild = (groupLabel, childLabel) =>
    onSortChange?.({ sortBy: childLabel, fieldsection: groupLabel || "" });

  return (
    <div style={{ backgroundColor: "white", padding: "8px", paddingBottom: "10px" }}>
      <OverviewToolbar
        styles={styles}
        actionSuggested={actionSuggested}
        options={normalizedOverview?.["Action Suggested"]?.action_filters_data || []}
        onChange={handleActionSuggestedChange}
        onExport={onExport}
        onCustomize={onCustomize}
        onExpand={() => setShowExpanded(true)}
        exportLoading={exportLoading}
        onSelectView={onSelectView}
        selectedView={selectedView}
      />

      {rows.length === 0 && !loading ? (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "#666", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          No data available
        </div>
      ) : (
        <>
          {/* Header (sticky) */}
          <div style={{ position: "sticky", top: 0, zIndex: 50, background: "#FFF" }}>
            <div className={styles.hideScrollbar} style={{ width: "100%", overflow: "hidden" }}>
              <TableRow
                component="div"
                className={`${styles.scrollRow} ${styles.hideScrollbar}`}
                sx={{ display: "flex", position: "relative", width: "100%", maxWidth: "100%", overflow: "visible", boxSizing: "border-box" }}
              >
                <HeaderRow
                  styles={styles}
                  fixedColumns={fixedColumns}
                  fixedPaneWidth={fixedPaneWidth}
                  FIXED_HEADER_HEIGHT={FIXED_HEADER_HEIGHT}
                  scrollableGroups={scrollableGroups}
                  rightPaneWidth={rightPaneWidth}
                  GROUP_BAR_HEIGHT={GROUP_BAR_HEIGHT}
                  SUBHEADER_HEIGHT={SUBHEADER_HEIGHT}
                  rightHeaderRef={rightHeaderRef}
                  sortBy={sortState.sortBy}
                  sortOrder={sortState.sortOrder}
                  fieldsection={sortState.fieldsection}
                  onLeftSort={handleSortLeft}
                  onRightChildSort={handleSortRightChild}
                  onCustomize={onCustomize}
                  onToggleFixedWidth={toggleFixedWidth}
                  expandedFixed={fxWidth}
                />
              </TableRow>
            </div>
          </div>

          {/* Bodies */}
          <div className={styles.bodyWrap}>
            <div style={{ display: "flex", width: "100%", position: "relative", boxSizing: "border-box" }}>
              <LeftBodyPane
                styles={styles}
                leftBodyRef={leftBodyRef}
                fixedColumns={fixedColumns}
                fixedPaneWidth={fixedPaneWidth}
                height={height}
                ROW_HEIGHT={ROW_HEIGHT}
                totalRow={totalRow}
                rows={rows}
                formatValue={formatValue}
              />
              <RightBodyPane
                styles={styles}
                rightBodyRef={rightBodyRef}
                rightPaneWidth={rightPaneWidth}
                height={height}
                ROW_HEIGHT={ROW_HEIGHT}
                totalRow={totalRow}
                rows={rows}
                scrollableGroups={scrollableGroups}
                getGroupObj={getGroupObj}
                getChildVal={getChildVal}
                formatValue={formatValue}
              />
            </div>
            {loading && (
              <div className={styles.bodyOverlay}>
                <div className={styles.loaderCard}>
                  <span className={styles.dot}></span>
                  <span className={styles.dot}></span>
                  <span className={styles.dot}></span>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          <PaginationFooter
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />

          {/* Expanded Overlay */}
          {showExpanded && (
            <ExpandedOverlay
              styles={styles}
              title="Overview (Expanded)"
              onClose={() => setShowExpanded(false)}
              fixedColumns={fixedColumns}
              fixedPaneWidth={fixedPaneWidth}
              rightPaneWidth={rightPaneWidth}
              rows={rows}
              totalRow={totalRow}
              scrollableGroups={scrollableGroups}
              getGroupObj={getGroupObj}
              getChildVal={getChildVal}
              formatValue={formatValue}
              sortState={sortState}
              onLeftSort={handleSortLeft}
              onRightChildSort={handleSortRightChild}
              onCustomize={onCustomize}
              FIXED_HEADER_HEIGHT={FIXED_HEADER_HEIGHT}
              GROUP_BAR_HEIGHT={GROUP_BAR_HEIGHT}
              SUBHEADER_HEIGHT={SUBHEADER_HEIGHT}
              onToggleFixedWidth={toggleFixedWidth}
              expandedFixed={fxWidth}
              loading={!!loading}
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

OverviewLossRecovery.propTypes = {
  overview: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  height: PropTypes.number,
  onActionSuggestedChange: PropTypes.func,
  onSortChange: PropTypes.func,
  sortState: PropTypes.object,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onCustomize: PropTypes.func,
  columns: PropTypes.array,
  onExport: PropTypes.func,
  exportLoading: PropTypes.bool,
  selectedAction: PropTypes.array,
  onSelectView: PropTypes.func,
  selectedView: PropTypes.string,
};

/* ------------------------- Expanded Overlay (lean) ------------------------- */

function ExpandedOverlay({
  styles,
  title,
  onClose,
  fixedColumns,
  fixedPaneWidth,
  rightPaneWidth,
  rows,
  totalRow,
  scrollableGroups,
  getGroupObj,
  getChildVal,
  formatValue,
  sortState,
  onLeftSort,
  onRightChildSort,
  onCustomize,
  FIXED_HEADER_HEIGHT,
  GROUP_BAR_HEIGHT,
  SUBHEADER_HEIGHT,
  onToggleFixedWidth, expandedFixed,
  loading,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onRowsPerPageChange
}) {
  const ROW_HEIGHT = 40;
  const expRightHeaderRef = React.useRef(null), expRightBodyRef = React.useRef(null), expLeftBodyRef = React.useRef(null);
  const [expLoading, setExpLoading] = React.useState(false);

  useSyncHorizontalScroll(expRightBodyRef, expRightHeaderRef);
  useSyncVerticalScroll(expLeftBodyRef, expRightBodyRef);
  useHeaderScrollbarCompensation(expRightHeaderRef, expRightBodyRef);
  useScrollToTopOnPageChange(expLeftBodyRef, expRightBodyRef, [page, pageSize]);

  useEffect(() => { if (!loading) setExpLoading(false); }, [loading]);
  useEffect(() => { setExpLoading(false); }, [rows, totalRow]);

  const handleLeftSortOverlay = (label) => { setExpLoading(true); onLeftSort?.(label); };
  const handleRightSortOverlay = (grp, child) => { setExpLoading(true); onRightChildSort?.(grp, child); };
  const handlePageChangeOverlay = (nextPage) => { setExpLoading(true); onPageChange?.(nextPage); };
  const handleRowsPerPageChangeOverlay = (size) => { setExpLoading(true); onRowsPerPageChange?.(size); };

  return (
    <div className={styles.expandOverlay}>
      <div className={`${styles.expandCard} ${styles.zoomMode}`}>
        <div className={styles.expandHeader}>
          <span className={styles.expandTitle}>{title}</span>
          <button type="button" onClick={onClose} className={styles.expandClose} title="Close">×</button>
        </div>

        <div className={styles.expandInner}>
          <div style={{ position: "sticky", top: 0, zIndex: 50, background: "#FFF" }}>
            <div className={styles.hideScrollbar} style={{ width: "100%", overflow: "hidden" }}>
              <TableRow
                component="div"
                className={`${styles.scrollRow} ${styles.hideScrollbar}`}
                sx={{ display: "flex", position: "relative", width: "100%", maxWidth: "100%", overflow: "visible", boxSizing: "border-box" }}
              >
                <HeaderRow
                  styles={styles}
                  fixedColumns={fixedColumns}
                  fixedPaneWidth={fixedPaneWidth}
                  FIXED_HEADER_HEIGHT={FIXED_HEADER_HEIGHT}
                  scrollableGroups={scrollableGroups}
                  rightPaneWidth={rightPaneWidth}
                  GROUP_BAR_HEIGHT={GROUP_BAR_HEIGHT}
                  SUBHEADER_HEIGHT={SUBHEADER_HEIGHT}
                  rightHeaderRef={expRightHeaderRef}
                  sortBy={sortState?.sortBy}
                  sortOrder={sortState?.sortOrder}
                  fieldsection={sortState?.fieldsection}
                  onLeftSort={handleLeftSortOverlay}
                  onRightChildSort={handleRightSortOverlay}
                  onCustomize={onCustomize}
                  onToggleFixedWidth={onToggleFixedWidth}
                  expandedFixed={expandedFixed}
                />
              </TableRow>
            </div>
          </div>

          <div className={styles.expandBodies}>
            <LeftBodyPane
              styles={styles}
              leftBodyRef={expLeftBodyRef}
              fixedColumns={fixedColumns}
              fixedPaneWidth={fixedPaneWidth}
              height={480}
              ROW_HEIGHT={ROW_HEIGHT}
              totalRow={totalRow}
              rows={rows}
              formatValue={formatValue}
            />
            <RightBodyPane
              styles={styles}
              rightBodyRef={expRightBodyRef}
              rightPaneWidth={rightPaneWidth}
              height={480}
              ROW_HEIGHT={ROW_HEIGHT}
              totalRow={totalRow}
              rows={rows}
              scrollableGroups={scrollableGroups}
              getGroupObj={getGroupObj}
              getChildVal={getChildVal}
              formatValue={formatValue}
            />
          </div>

          <PaginationFooter
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={handlePageChangeOverlay}
            onRowsPerPageChange={handleRowsPerPageChangeOverlay}
          />

          {(expLoading || loading) && (
            <div className={styles.bodyOverlay}>
              <div className={styles.loaderCard}>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ExpandedOverlay.propTypes = {
  styles: PropTypes.object,
  title: PropTypes.string,
  onClose: PropTypes.func,
  fixedColumns: PropTypes.array,
  fixedPaneWidth: PropTypes.number,
  rightPaneWidth: PropTypes.string,
  rows: PropTypes.array,
  totalRow: PropTypes.object,
  scrollableGroups: PropTypes.array,
  getGroupObj: PropTypes.func,
  getChildVal: PropTypes.func,
  formatValue: PropTypes.func,
  sortState: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
    fieldsection: PropTypes.string,
  }),
  onLeftSort: PropTypes.func,
  onRightChildSort: PropTypes.func,
  onCustomize: PropTypes.func,
  FIXED_HEADER_HEIGHT: PropTypes.number,
  GROUP_BAR_HEIGHT: PropTypes.number,
  SUBHEADER_HEIGHT: PropTypes.number,
  onToggleFixedWidth: PropTypes.func,
  expandedFixed: PropTypes.object,
  loading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};